<template>
  <article id="lotto-wrapper" class="contents-wrapper">
    <div id="lotto-analysis">
      <!-- 로딩 -->
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <div class="loading-text">데이터를 불러오는 중...</div>
      </div>

      <template v-if="!loading">
        <!-- 페이지 헤더 -->
        <div class="analysis-head">
          <h2 class="analysis-title">번호 분석</h2>
          <p class="analysis-sub">역대 당첨 데이터로 번호의 출현 패턴을 살펴봅니다. 재미로 보는 참고 지표이며, 당첨을 보장하지 않아요.</p>
        </div>

        <!-- 출현빈도 + 히트맵 같은 행 -->
        <div class="charts-row" v-if="numberCountList.length > 0">
          <!-- 섹션 1: 번호별 출현빈도 바 차트 -->
          <div class="section-card frequency-card">
            <div class="chart-header">
              <div class="section-card-title">번호별 출현빈도</div>
              <div class="sort-buttons">
                <button :class="{ active: sortMode === 'number' }" @click="sortMode = 'number'">번호순</button>
                <button :class="{ active: sortMode === 'desc' }" @click="sortMode = 'desc'">많은순</button>
                <button :class="{ active: sortMode === 'asc' }" @click="sortMode = 'asc'">적은순</button>
              </div>
            </div>
            <p class="section-desc">각 번호가 1등 당첨에 나온 총 횟수입니다(막대 뒤 연한 부분은 보너스). 길수록 자주 나왔어요.</p>
            <div class="frequency-chart">
              <div
                v-for="(item, idx) in sortedNumberCount"
                :key="'bar-' + item.number"
                class="frequency-bar-wrapper"
              >
                <div v-if="sortMode !== 'number'" class="bar-rank">{{ idx + 1 }}</div>
                <div :class="getColorClass(item.number)" class="lotto-ball bar-ball">
                  <div class="number">{{ item.number }}</div>
                </div>
                <div class="bar-track">
                  <div
                    class="bar-fill"
                    :style="{ width: getBarWidth(item.count) + '%' }"
                  ></div>
                  <div
                    v-if="item.bonusCount > 0"
                    class="bar-fill-bonus"
                    :style="{ left: getBarWidth(item.count) + '%', width: getBarWidth(item.bonusCount) + '%' }"
                  ></div>
                </div>
                <div class="bar-count">{{ item.count }}+{{ item.bonusCount }}</div>
              </div>
            </div>
          </div>

          <!-- 섹션 2: 동반출현 히트맵 -->
          <div class="section-card heatmap-card" v-if="accompanyList.length > 0">
            <div class="section-card-title">동반출현 히트맵</div>
            <p class="section-desc">두 번호가 같은 회차에 함께 나온 빈도. 칸이 진할수록 자주 함께 나온 조합이에요.</p>
            <div class="heatmap-wrapper">
              <div class="heatmap-grid">
                <div class="heatmap-label corner"></div>
                <div v-for="n in 45" :key="'hl-' + n" class="heatmap-label">{{ n }}</div>
                <template v-for="row in 45" :key="'hr-' + row">
                  <div class="heatmap-label">{{ row }}</div>
                  <div
                    v-for="col in 45"
                    :key="'hc-' + row + '-' + col"
                    class="heatmap-cell"
                    :class="row === col ? 'heat-diagonal' : getHeatClass(row, col)"
                    @mouseenter="showHeatmapTooltip($event, row, col)"
                    @mouseleave="hideHeatmapTooltip"
                  ></div>
                </template>
              </div>
            </div>
            <div class="heatmap-legend">
              <span>적음</span>
              <div class="legend-block heat-1"></div>
              <div class="legend-block heat-2"></div>
              <div class="legend-block heat-3"></div>
              <div class="legend-block heat-4"></div>
              <div class="legend-block heat-5"></div>
              <span>많음</span>
            </div>
            <div
              v-if="tooltipVisible"
              class="heatmap-tooltip"
              :style="{ top: tooltipY + 'px', left: tooltipX + 'px' }"
            >
              {{ tooltipText }}
            </div>
          </div>
        </div>

        <!-- 섹션 2-1: 당첨번호 빈도 티어 분석 -->
        <div class="section-card" v-if="tierAnalysisRows.length > 0">
          <div class="section-card-title">당첨번호 빈도 티어 분석 <span class="title-meta">최근 50회</span></div>
          <p class="section-desc">전체 번호를 출현빈도 상·중·하위로 나눴을 때, 매 회차 당첨번호가 각 그룹에서 몇 개씩 나왔는지 보여줍니다.</p>
          <div class="tier-summary">
            <div class="tier-summary-item">
              <div class="tier-label tier-high">상위 빈도</div>
              <div class="tier-value">{{ avgTierRatio.high }}%</div>
              <div class="tier-numbers">
                <span
                  v-for="n in tierGroupNumbers.high"
                  :key="'tsh-' + n"
                  class="tier-num-badge"
                  :class="getColorClass(n)"
                >{{ n }}</span>
              </div>
            </div>
            <div class="tier-summary-item">
              <div class="tier-label tier-mid">중위 빈도</div>
              <div class="tier-value">{{ avgTierRatio.mid }}%</div>
              <div class="tier-numbers">
                <span
                  v-for="n in tierGroupNumbers.mid"
                  :key="'tsm-' + n"
                  class="tier-num-badge"
                  :class="getColorClass(n)"
                >{{ n }}</span>
              </div>
            </div>
            <div class="tier-summary-item">
              <div class="tier-label tier-low">하위 빈도</div>
              <div class="tier-value">{{ avgTierRatio.low }}%</div>
              <div class="tier-numbers">
                <span
                  v-for="n in tierGroupNumbers.low"
                  :key="'tsl-' + n"
                  class="tier-num-badge"
                  :class="getColorClass(n)"
                >{{ n }}</span>
              </div>
            </div>
          </div>
          <div class="tier-table-wrapper">
            <table class="tier-table tier-detail-table">
              <thead>
                <tr>
                  <th>회차</th>
                  <th>당첨번호</th>
                  <th>상위</th>
                  <th>중위</th>
                  <th>하위</th>
                  <th>비율</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in visibleTierRows" :key="'tier-' + row.drwNo">
                  <td>{{ row.drwNo }}</td>
                  <td>
                    <div class="tier-balls">
                      <div
                        v-for="(num, i) in row.numbers"
                        :key="'tb-' + row.drwNo + '-' + i"
                        class="tier-ball"
                        :class="getColorClass(num)"
                      >
                        {{ num }}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="tier-cell-detail">
                      <span class="tier-cell-count">{{ row.high }}</span>
                      <div class="tier-cell-nums" v-if="row.highNums.length > 0">
                        <span v-for="n in row.highNums" :key="'rh-' + row.drwNo + '-' + n" class="tier-mini-ball" :class="getColorClass(n)">{{ n }}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="tier-cell-detail">
                      <span class="tier-cell-count">{{ row.mid }}</span>
                      <div class="tier-cell-nums" v-if="row.midNums.length > 0">
                        <span v-for="n in row.midNums" :key="'rm-' + row.drwNo + '-' + n" class="tier-mini-ball" :class="getColorClass(n)">{{ n }}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="tier-cell-detail">
                      <span class="tier-cell-count">{{ row.low }}</span>
                      <div class="tier-cell-nums" v-if="row.lowNums.length > 0">
                        <span v-for="n in row.lowNums" :key="'rl-' + row.drwNo + '-' + n" class="tier-mini-ball" :class="getColorClass(n)">{{ n }}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="tier-stack-bar">
                      <div class="stack-high" :style="{ width: (row.high / 6 * 100) + '%' }"><span v-if="row.high > 0" class="stack-label">상위</span></div>
                      <div class="stack-mid" :style="{ width: (row.mid / 6 * 100) + '%' }"><span v-if="row.mid > 0" class="stack-label">중위</span></div>
                      <div class="stack-low" :style="{ width: (row.low / 6 * 100) + '%' }"><span v-if="row.low > 0" class="stack-label">하위</span></div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <button v-if="tierAnalysisRows.length > 10" class="expand-btn" @click="tierExpanded = !tierExpanded">
            {{ tierExpanded ? '접기' : '더보기 (' + tierAnalysisRows.length + '회)' }}
          </button>
        </div>

        <!-- 섹션 3: 홀짝/번호대 분석 -->
        <div class="section-card" v-if="oddEvenRows.length > 0">
          <div class="section-card-title">홀짝 · 번호대 분석 <span class="title-meta">최근 50회</span></div>
          <p class="section-desc">회차마다 홀수/짝수가 몇 개였는지, 번호가 낮은·중간·높은 구간에 어떻게 분포했는지 보여줍니다.</p>
          <div class="oe-hl-summary">
            <div class="oe-hl-item">
              <div class="oe-hl-label">평균 홀수</div>
              <div class="oe-hl-value">{{ avgOddEven.odd }}개</div>
            </div>
            <div class="oe-hl-item">
              <div class="oe-hl-label">평균 짝수</div>
              <div class="oe-hl-value">{{ avgOddEven.even }}개</div>
            </div>
          </div>
          <div class="oe-hl-divider"></div>
          <div class="oe-hl-summary">
            <div class="oe-hl-item">
              <div class="oe-hl-label">낮은숫자 (1~15)</div>
              <div class="oe-hl-value">{{ avgNumRange.low }}개</div>
            </div>
            <div class="oe-hl-item">
              <div class="oe-hl-label">중간숫자 (16~30)</div>
              <div class="oe-hl-value">{{ avgNumRange.mid }}개</div>
            </div>
            <div class="oe-hl-item">
              <div class="oe-hl-label">높은숫자 (31~45)</div>
              <div class="oe-hl-value">{{ avgNumRange.high }}개</div>
            </div>
          </div>
          <div class="tier-table-wrapper">
            <table class="tier-table">
              <thead>
                <tr>
                  <th>회차</th>
                  <th>당첨번호</th>
                  <th>홀</th>
                  <th>짝</th>
                  <th>낮</th>
                  <th>중</th>
                  <th>높</th>
                  <th>합계</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in visibleOddEvenRows" :key="'oe-' + row.drwNo">
                  <td>{{ row.drwNo }}</td>
                  <td>
                    <div class="tier-balls">
                      <div
                        v-for="(num, i) in row.numbers"
                        :key="'oeb-' + row.drwNo + '-' + i"
                        class="tier-ball"
                        :class="getColorClass(num)"
                      >
                        {{ num }}
                      </div>
                    </div>
                  </td>
                  <td>{{ row.odd }}</td>
                  <td>{{ row.even }}</td>
                  <td>{{ row.lowRange }}</td>
                  <td>{{ row.midRange }}</td>
                  <td>{{ row.highRange }}</td>
                  <td>{{ row.sum }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <button v-if="oddEvenRows.length > 10" class="expand-btn" @click="oeExpanded = !oeExpanded">
            {{ oeExpanded ? '접기' : '더보기 (' + oddEvenRows.length + '회)' }}
          </button>
        </div>

        <!-- 섹션 4: 끝수 분석 -->
        <div class="section-card" v-if="lastDigitData.length > 0">
          <div class="section-card-title">끝수별 출현빈도 <span class="title-meta">최근 50회</span></div>
          <p class="section-desc">번호의 일의 자리(끝수, 예: 7·17·27·37)별로 얼마나 나왔는지 모은 것입니다.</p>
          <div class="last-digit-chart">
            <div v-for="item in lastDigitData" :key="'ld-' + item.digit" class="last-digit-bar-wrapper">
              <div class="last-digit-label">{{ item.digit }}</div>
              <div class="bar-track">
                <div class="bar-fill" :style="{ width: (item.count / maxLastDigit * 100) + '%' }"></div>
              </div>
              <div class="bar-count">{{ item.count }}회</div>
            </div>
          </div>
        </div>

        <!-- 섹션 5: 연번/합계 분석 -->
        <div class="section-card" v-if="consecutiveData.length > 0">
          <div class="section-card-title">연번 · 합계 분석 <span class="title-meta">최근 50회</span></div>
          <p class="section-desc">연속된 번호(예: 14·15)가 포함된 비율과, 6개 번호 합계가 어느 구간에 몰리는지 보여줍니다.</p>
          <div class="consecutive-summary">
            <div class="consecutive-item">
              <div class="consecutive-label">연번 포함 비율</div>
              <div class="consecutive-value">{{ consecutiveRate }}%</div>
            </div>
            <div class="consecutive-item">
              <div class="consecutive-label">평균 합계</div>
              <div class="consecutive-value">{{ avgSum }}</div>
            </div>
            <div class="consecutive-item">
              <div class="consecutive-label">합계 범위</div>
              <div class="consecutive-value">{{ sumRange.min }} ~ {{ sumRange.max }}</div>
            </div>
          </div>
          <div class="sum-chart-container">
            <Bar :data="sumChartData" :options="sumChartOptions" />
          </div>
        </div>

        <!-- 섹션 6: 회차별 1등 당첨금 추이 -->
        <div class="section-card" v-if="lottoResults.length > 0">
          <div class="section-card-title">1등 당첨금 추이 <span class="title-meta">최근 100회</span></div>
          <p class="section-desc">회차별 1등 1인당 당첨금(억원)의 변화입니다. 판매량·당첨자 수에 따라 오르내려요.</p>
          <div class="chart-container">
            <Line :data="prizeChartData" :options="prizeChartOptions" />
          </div>
        </div>

      </template>
    </div>
  </article>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import UtilMixin from "@/common/mixins/UtilMixin";
import {
  LottoNumberCount,
  LottoAccompanyCount,
  LottoResultResponse,
} from "../class";
import { Line, Bar } from "vue-chartjs";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler);

interface TierRow {
  drwNo: number;
  numbers: number[];
  high: number;
  mid: number;
  low: number;
  highNums: number[];
  midNums: number[];
  lowNums: number[];
}

interface OddEvenRow {
  drwNo: number;
  numbers: number[];
  odd: number;
  even: number;
  lowRange: number;
  midRange: number;
  highRange: number;
  sum: number;
}

interface LastDigitItem {
  digit: number;
  count: number;
}

interface ConsecutiveItem {
  drwNo: number;
  hasConsecutive: boolean;
  consecutivePairs: string[];
}

export default defineComponent({
  name: "LottoAnalysisView",
  components: { Line, Bar },
  mixins: [UtilMixin],
  data() {
    return {
      loading: true,
      sortMode: "number" as "number" | "desc" | "asc",
      numberCountList: [] as LottoNumberCount[],
      accompanyList: [] as LottoAccompanyCount[],
      accompanyMatrix: {} as Record<string, number>,
      maxAccompany: 1,
      lottoResults: [] as LottoResultResponse[],
      // heatmap tooltip
      tooltipVisible: false,
      tooltipX: 0,
      tooltipY: 0,
      tooltipText: "",
      tierExpanded: false,
      oeExpanded: false,
    };
  },
  computed: {
    recentResults(): LottoResultResponse[] {
      return [...this.lottoResults]
        .sort((a, b) => b.drwNo - a.drwNo)
        .slice(0, 50);
    },
    sortedNumberCount(): LottoNumberCount[] {
      const list = [...this.numberCountList];
      if (this.sortMode === "number") {
        return list.sort((a, b) => a.number - b.number);
      } else if (this.sortMode === "desc") {
        return list.sort((a, b) => b.count - a.count);
      } else {
        return list.sort((a, b) => a.count - b.count);
      }
    },
    maxCount(): number {
      if (this.numberCountList.length === 0) return 1;
      return Math.max(...this.numberCountList.map((n) => n.count + n.bonusCount));
    },
    // 홀짝/고저 분석
    oddEvenRows(): OddEvenRow[] {
      return this.recentResults.map((r) => {
        const numbers = [r.drwtNo1, r.drwtNo2, r.drwtNo3, r.drwtNo4, r.drwtNo5, r.drwtNo6];
        const odd = numbers.filter((n) => n % 2 === 1).length;
        const even = 6 - odd;
        const lowRange = numbers.filter((n) => n >= 1 && n <= 15).length;
        const midRange = numbers.filter((n) => n >= 16 && n <= 30).length;
        const highRange = numbers.filter((n) => n >= 31 && n <= 45).length;
        const sum = numbers.reduce((a, b) => a + b, 0);
        return { drwNo: r.drwNo, numbers, odd, even, lowRange, midRange, highRange, sum };
      });
    },
    avgOddEven(): { odd: string; even: string } {
      if (this.oddEvenRows.length === 0) return { odd: "0", even: "0" };
      const total = this.oddEvenRows.length;
      const sumOdd = this.oddEvenRows.reduce((s, r) => s + r.odd, 0);
      return {
        odd: (sumOdd / total).toFixed(1),
        even: ((total * 6 - sumOdd) / total).toFixed(1),
      };
    },
    tierGroupNumbers(): { high: number[]; mid: number[]; low: number[] } {
      const high: number[] = [];
      const mid: number[] = [];
      const low: number[] = [];
      for (const [num, tier] of Object.entries(this.tierMap)) {
        const n = Number(num);
        if (tier === "high") high.push(n);
        else if (tier === "mid") mid.push(n);
        else low.push(n);
      }
      return {
        high: high.sort((a, b) => a - b),
        mid: mid.sort((a, b) => a - b),
        low: low.sort((a, b) => a - b),
      };
    },
    visibleOddEvenRows(): OddEvenRow[] {
      return this.oeExpanded ? this.oddEvenRows : this.oddEvenRows.slice(0, 10);
    },
    visibleTierRows(): TierRow[] {
      return this.tierExpanded ? this.tierAnalysisRows : this.tierAnalysisRows.slice(0, 10);
    },
    avgNumRange(): { low: string; mid: string; high: string } {
      if (this.oddEvenRows.length === 0) return { low: "0", mid: "0", high: "0" };
      const total = this.oddEvenRows.length;
      const sumLow = this.oddEvenRows.reduce((s, r) => s + r.lowRange, 0);
      const sumMid = this.oddEvenRows.reduce((s, r) => s + r.midRange, 0);
      const sumHigh = this.oddEvenRows.reduce((s, r) => s + r.highRange, 0);
      return {
        low: (sumLow / total).toFixed(1),
        mid: (sumMid / total).toFixed(1),
        high: (sumHigh / total).toFixed(1),
      };
    },
    // 끝수 분석
    lastDigitData(): LastDigitItem[] {
      if (this.recentResults.length === 0) return [];
      const digitMap: Record<number, number> = {};
      for (let d = 0; d <= 9; d++) digitMap[d] = 0;
      for (const r of this.recentResults) {
        const nums = [r.drwtNo1, r.drwtNo2, r.drwtNo3, r.drwtNo4, r.drwtNo5, r.drwtNo6];
        for (const n of nums) {
          digitMap[n % 10]++;
        }
      }
      return Object.entries(digitMap).map(([d, c]) => ({ digit: Number(d), count: c }));
    },
    maxLastDigit(): number {
      if (this.lastDigitData.length === 0) return 1;
      return Math.max(...this.lastDigitData.map((d) => d.count));
    },
    // 연번 분석
    consecutiveData(): ConsecutiveItem[] {
      return this.recentResults.map((r) => {
        const nums = [r.drwtNo1, r.drwtNo2, r.drwtNo3, r.drwtNo4, r.drwtNo5, r.drwtNo6].sort((a, b) => a - b);
        const pairs: string[] = [];
        for (let i = 0; i < nums.length - 1; i++) {
          if (nums[i + 1] - nums[i] === 1) {
            pairs.push(`${nums[i]}-${nums[i + 1]}`);
          }
        }
        return { drwNo: r.drwNo, hasConsecutive: pairs.length > 0, consecutivePairs: pairs };
      });
    },
    consecutiveRate(): string {
      if (this.consecutiveData.length === 0) return "0";
      const count = this.consecutiveData.filter((c) => c.hasConsecutive).length;
      return ((count / this.consecutiveData.length) * 100).toFixed(1);
    },
    avgSum(): string {
      if (this.oddEvenRows.length === 0) return "0";
      const total = this.oddEvenRows.reduce((s, r) => s + r.sum, 0);
      return (total / this.oddEvenRows.length).toFixed(0);
    },
    sumRange(): { min: number; max: number } {
      if (this.oddEvenRows.length === 0) return { min: 0, max: 0 };
      const sums = this.oddEvenRows.map((r) => r.sum);
      return { min: Math.min(...sums), max: Math.max(...sums) };
    },
    sumChartData(): any {
      // 합계 구간별 분포 (21~100 → ~70, 71~100, 101~130, 131~160, 161~)
      const ranges = [
        { label: "21~70", min: 21, max: 70 },
        { label: "71~100", min: 71, max: 100 },
        { label: "101~130", min: 101, max: 130 },
        { label: "131~170", min: 131, max: 170 },
        { label: "171~255", min: 171, max: 255 },
      ];
      const counts = ranges.map((r) =>
        this.oddEvenRows.filter((row) => row.sum >= r.min && row.sum <= r.max).length,
      );
      return {
        labels: ranges.map((r) => r.label),
        datasets: [
          {
            label: "횟수",
            data: counts,
            backgroundColor: ["#e3f2fd", "#90caf9", "#42a5f5", "#1e88e5", "#1565c0"],
            borderRadius: 4,
          },
        ],
      };
    },
    sumChartOptions(): any {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
        },
        scales: {
          x: { ticks: { font: { size: 10 } } },
          y: { beginAtZero: true, ticks: { stepSize: 1, font: { size: 10 } } },
        },
      };
    },
    // 티어: 빈도순 상위 15, 중위 15, 하위 15
    tierMap(): Record<number, "high" | "mid" | "low"> {
      const sorted = [...this.numberCountList].sort((a, b) => b.count - a.count);
      const map: Record<number, "high" | "mid" | "low"> = {};
      sorted.forEach((item, idx) => {
        if (idx < 15) map[item.number] = "high";
        else if (idx < 30) map[item.number] = "mid";
        else map[item.number] = "low";
      });
      return map;
    },
    tierAnalysisRows(): TierRow[] {
      return this.recentResults.map((r) => {
        const numbers = [r.drwtNo1, r.drwtNo2, r.drwtNo3, r.drwtNo4, r.drwtNo5, r.drwtNo6];
        let high = 0, mid = 0, low = 0;
        const highNums: number[] = [], midNums: number[] = [], lowNums: number[] = [];
        for (const n of numbers) {
          const tier = this.tierMap[n];
          if (tier === "high") { high++; highNums.push(n); }
          else if (tier === "mid") { mid++; midNums.push(n); }
          else { low++; lowNums.push(n); }
        }
        return { drwNo: r.drwNo, numbers, high, mid, low, highNums, midNums, lowNums };
      });
    },
    avgTierRatio(): { high: string; mid: string; low: string } {
      if (this.tierAnalysisRows.length === 0) {
        return { high: "0", mid: "0", low: "0" };
      }
      const total = this.tierAnalysisRows.length;
      const sumHigh = this.tierAnalysisRows.reduce((s, r) => s + r.high, 0);
      const sumMid = this.tierAnalysisRows.reduce((s, r) => s + r.mid, 0);
      const sumLow = this.tierAnalysisRows.reduce((s, r) => s + r.low, 0);
      const totalNumbers = total * 6;
      return {
        high: ((sumHigh / totalNumbers) * 100).toFixed(1),
        mid: ((sumMid / totalNumbers) * 100).toFixed(1),
        low: ((sumLow / totalNumbers) * 100).toFixed(1),
      };
    },
    prizeChartData(): any {
      const recent = [...this.lottoResults]
        .sort((a, b) => a.drwNo - b.drwNo)
        .slice(-100);
      return {
        labels: recent.map((r) => r.drwNo + "회"),
        datasets: [
          {
            label: "1등 당첨금 (억원)",
            data: recent.map((r) => Math.round(r.firstWinamnt / 100000000)),
            borderColor: "#eab13a",
            backgroundColor: "rgba(234, 177, 58, 0.12)",
            fill: true,
            tension: 0.3,
            pointRadius: 2,
            pointHoverRadius: 5,
          },
        ],
      };
    },
    prizeChartOptions(): any {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx: any) => ctx.parsed.y + "억원",
            },
          },
        },
        scales: {
          x: {
            ticks: { maxTicksLimit: 10, font: { size: 10 } },
          },
          y: {
            beginAtZero: true,
            ticks: {
              callback: (value: number) => value + "억",
              font: { size: 10 },
            },
          },
        },
      };
    },
  },
  methods: {
    getColorClass(number: number): string {
      if (number >= 1 && number <= 9) return "bg-yellow-400";
      if (number >= 10 && number <= 19) return "bg-blue-400";
      if (number >= 20 && number <= 29) return "bg-red-400";
      if (number >= 30 && number <= 39) return "bg-gray-400";
      if (number >= 40 && number <= 45) return "bg-green-400";
      return "";
    },
    getBarWidth(count: number): number {
      return (count / this.maxCount) * 100;
    },
    buildAccompanyMatrix() {
      const matrix: Record<string, number> = {};
      let max = 0;
      for (const item of this.accompanyList) {
        const key1 = `${item.drwtNo1}-${item.drwtNo2}`;
        const key2 = `${item.drwtNo2}-${item.drwtNo1}`;
        matrix[key1] = item.showCount;
        matrix[key2] = item.showCount;
        if (item.showCount > max) max = item.showCount;
      }
      this.accompanyMatrix = matrix;
      this.maxAccompany = max || 1;
    },
    getHeatClass(row: number, col: number): string {
      const key = `${row}-${col}`;
      const count = this.accompanyMatrix[key] || 0;
      if (count === 0) return "heat-0";
      const ratio = count / this.maxAccompany;
      if (ratio <= 0.2) return "heat-1";
      if (ratio <= 0.4) return "heat-2";
      if (ratio <= 0.6) return "heat-3";
      if (ratio <= 0.8) return "heat-4";
      return "heat-5";
    },
    showHeatmapTooltip(event: MouseEvent, row: number, col: number) {
      if (row === col) return;
      const key = `${row}-${col}`;
      const count = this.accompanyMatrix[key] || 0;
      this.tooltipText = `${row}번 + ${col}번: ${count}회`;
      this.tooltipX = event.clientX + 10;
      this.tooltipY = event.clientY - 30;
      this.tooltipVisible = true;
    },
    hideHeatmapTooltip() {
      this.tooltipVisible = false;
    },
    async fetchData() {
      this.loading = true;
      try {
        const [ncRes, acRes, resultRes] = await Promise.all([
          this.getApi().lotto().getNumberCount(true),
          this.getApi().lotto().getAccompanyCount(true),
          this.getApi().lotto().getAllLottoResults(1, 100),
        ]);

        this.numberCountList = ncRes.numberCountList;
        this.accompanyList = acRes.accompanyList;
        this.buildAccompanyMatrix();

        if (resultRes && Array.isArray(resultRes.content)) {
          this.lottoResults = resultRes.content.map((item: any) => {
            const r = new LottoResultResponse();
            r.init(item);
            return r;
          });
        }
      } catch (e) {
        console.error("LottoAnalysisView fetchData error:", e);
      } finally {
        this.loading = false;
      }
    },
  },
  created() {
    window.scrollTo(0, 0);
    this.fetchData();
  },
});
</script>

<style lang="scss" scoped></style>
